import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useRef,
} from 'react'
import clsx from 'clsx'
import { Helmet } from 'react-helmet'
import { useLocation, useParams, useRouteMatch } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import AddIcon from 'components/material-symbol/icons/Add'
import { Channel, ChannelList, useChatContext, Thread } from 'stream-chat-react'
import MessagingEmojiPicker from './messaging-emoji-picker'
import { withStreamClient } from 'lib/stream-messaging'
import { useDebounce, useMobileHeight } from 'lib/hooks'
import ChannelPreview from './channel-preview'
import { getMessagingLatestMessagePreview } from './messaging-channel-preview-attachment'
import {
  ChannelInner,
  CustomAttachment,
  CustomMessage,
  CustomPinIndicator,
} from './channel-inner'
import NewConversationDialog from './new-conversation-dialog'
import EmptyChannel from './empty-channel'
import { AttachmentContextProvider } from './use-attachment-context'
import ConversationLoader from './components/conversation-loader'
import ChannelListHeader from './channel-list-header'
import { ChannelListContext } from './channel-list-context'
import { FollowUpSentProvider } from './follow-up-sent-context'
import MessagingSendButton from './messaging-send-button'
import MessagingSendToChannelCheckbox from './messaging-send-to-channel-checkbox'
import MessagingMessageOptions from './messaging-message-options'
import MessagingMessageActionsBox from './messaging-message-actions-box'
import MessagingFileUploadIcon from './messaging-file-upload-icon'
import MessagingAvatar from './messaging-avatar'
import MessagingLoadMorePaginator from './messaging-load-more-paginator'
import MessagingThreadHeader from './messaging-thread-header'
import { PageHeader } from '../page-header'
import { useAuth } from 'lib/auth'
import { isGroupChannel } from './utils'

const COMPACT_LAYOUT_MAX_WIDTH = 640

const useStyles = makeStyles(() => ({
  pageHeaderActionButton: {
    minWidth: 40,
    width: 40,
    height: 40,
    padding: 0,
    borderRadius: '50px',
    border: '1px solid #E0E0E0',
    backgroundColor: '#FFFFFF',
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: '#E0E0E0',
      boxShadow: 'none',
    },
  },
}))

const Messenger = withStreamClient(() => {
  const { channelId: initialChannelId } = useParams()
  const { pathname } = useLocation()
  const { path } = useRouteMatch()
  const [searchQuery, setSearchQuery] = useState('')
  const [listTab, setListTab] = useState('inbox')
  const [loading, setLoading] = useState(true)
  const [displayChannelList, setDisplayChannelList] = useState(false)
  const [inboxCount, setInboxCount] = useState(0)
  const [hiddenCount, setHiddenCount] = useState(0)
  const [isCompactLayout, setIsCompactLayout] = useState(false)
  const debouncedSearchQuery = useDebounce(searchQuery, 300)?.trim()
  const { channel, client, setActiveChannel } = useChatContext()
  const theme = useTheme()
  const classes = useStyles()
  const isSmallViewport = useMediaQuery(theme.breakpoints.down('sm'), {
    defaultMatches: false,
    noSsr: true,
  })
  const showChannelList = !isCompactLayout || !channel
  const showChannel = Boolean(channel)
  const { user } = useAuth()
  useMobileHeight()
  const isGroupConversation = isGroupChannel(channel)

  const channelListContextValue = useMemo(
    () => ({
      searchQuery,
      setSearchQuery,
      listTab,
      setListTab,
      inboxCount,
      hiddenCount,
    }),
    [searchQuery, listTab, inboxCount, hiddenCount],
  )

  const newConversationDialogTrigger = isSmallViewport ? (
    <Button
      variant="outlined"
      color="default"
      className={classes.pageHeaderActionButton}
      aria-label="New Message"
    >
      <AddIcon />
    </Button>
  ) : (
    <Button variant="contained" color="primary" startIcon={<AddIcon />}>
      New Message
    </Button>
  )

  const inboxFilters = useMemo(
    () => ({
      members: { $in: [client.user?.id] },
      frozen: false,
      hidden: false,
      ...(debouncedSearchQuery && {
        'member.user.name': { $autocomplete: debouncedSearchQuery },
      }),
      last_message_at: { $gt: '1970-01-01T00:00:00Z' },
    }),
    [client.user?.id, debouncedSearchQuery],
  )

  const hiddenFilters = useMemo(
    () => ({
      members: { $in: [client.user?.id] },
      frozen: false,
      hidden: true,
      ...(debouncedSearchQuery && {
        'member.user.name': { $autocomplete: debouncedSearchQuery },
      }),
    }),
    [client.user?.id, debouncedSearchQuery],
  )

  const filters = listTab === 'hidden' ? hiddenFilters : inboxFilters
  const sort = useMemo(() => ({ last_message_at: -1 }), [])
  const options = { limit: 20 }

  useEffect(() => {
    if (displayChannelList) return undefined

    if (initialChannelId || channel?.id) {
      setDisplayChannelList(true)
      return undefined
    }

    if (!client?.user?.id) return undefined

    let cancelled = false

    const hasChannels = async () => {
      try {
        const [inboxChannels, hiddenChannels] = await Promise.all([
          client.queryChannels(inboxFilters, sort, { limit: 1 }),
          client.queryChannels(hiddenFilters, sort, { limit: 1 }),
        ])
        if (!cancelled) {
          const hasInboxChannels = inboxChannels.length > 0
          const hasHiddenChannels = hiddenChannels.length > 0
          setDisplayChannelList(hasInboxChannels || hasHiddenChannels)
          if (!hasInboxChannels && hasHiddenChannels) {
            setListTab('hidden')
          }
        }
      } catch {
        // Leave the empty state in place when the channel query fails.
      }
    }

    hasChannels()

    return () => {
      cancelled = true
    }
  }, [
    channel?.id,
    client,
    client?.user?.id,
    displayChannelList,
    hiddenFilters,
    inboxFilters,
    initialChannelId,
    sort,
  ])

  useEffect(() => {
    if (!client?.user?.id) return undefined

    const refreshCounts = async () => {
      const [inboxChannels, hiddenChannels] = await Promise.all([
        client.queryChannels(
          {
            members: { $in: [client.user.id] },
            frozen: false,
            hidden: false,
            last_message_at: { $gt: '1970-01-01T00:00:00Z' },
          },
          sort,
          { limit: 100 },
        ),
        client.queryChannels(
          {
            members: { $in: [client.user.id] },
            frozen: false,
            hidden: true,
          },
          sort,
          { limit: 100 },
        ),
      ])
      setInboxCount(inboxChannels.length)
      setHiddenCount(hiddenChannels.length)
    }

    refreshCounts()

    if (typeof client.on !== 'function') return undefined

    const handleChannelChange = () => {
      refreshCounts()
    }

    client.on('notification.message_new', handleChannelChange)
    client.on('notification.added_to_channel', handleChannelChange)
    client.on('notification.removed_from_channel', handleChannelChange)
    client.on('channel.hidden', handleChannelChange)
    client.on('channel.visible', handleChannelChange)

    return () => {
      client.off('notification.message_new', handleChannelChange)
      client.off('notification.added_to_channel', handleChannelChange)
      client.off('notification.removed_from_channel', handleChannelChange)
      client.off('channel.hidden', handleChannelChange)
      client.off('channel.visible', handleChannelChange)
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [client, channel?.id])

  // if channelId parameter is provided, select the corresponding channel on load
  useEffect(() => {
    if (initialChannelId != null) {
      const updateActiveChannel = async () => {
        const filter = { id: initialChannelId }
        const sort = []
        const [ch] = await client.queryChannels(filter, sort, { limit: 1 })
        if (ch != null) {
          setActiveChannel(ch)
          if (ch?.data?.hidden || ch?.hidden) {
            setListTab('hidden')
          }
        }
      }

      updateActiveChannel()
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [])

  useEffect(() => {
    // Defer until after React re-renders the channel. Scroll the message list
    // to the latest message. Keep scroll contained inside the messaging layout
    // so the page shell does not move.
    setTimeout(() => {
      const hasMessages = channel?.state?.messages?.length > 0
      const streamList = document.querySelector(
        '.messaging-page .str-chat__list',
      )
      if (streamList) {
        streamList.scrollTop = hasMessages ? streamList.scrollHeight : 0
      }
    }, 0)
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [channel])

  // update browser url to match selected channel whenever it changes
  useEffect(() => {
    if (channel != null && path.includes(':channelId?')) {
      window.history.replaceState(
        null,
        null,
        path.replace(':channelId?', channel.id),
      )
    }
  }, [channel, path])

  // Keep --page-header-bottom in sync with the page header's actual bottom edge so
  // the tablet channel-list panel sits precisely below it. displayChannelList is a
  // dep because the ref target only exists in the DOM after that flag turns true.
  const pageHeaderWrapperRef = useRef(null)
  const messagingPageRef = useRef(null)
  useLayoutEffect(() => {
    const headerEl = pageHeaderWrapperRef.current
    const pageEl = messagingPageRef.current
    if (!headerEl || !pageEl) return

    const update = () => {
      const pageTop = pageEl.getBoundingClientRect().top
      const pageWidth = pageEl.getBoundingClientRect().width
      const availableHeight = Math.max(0, window.innerHeight - pageTop)
      if (availableHeight <= 0) return

      pageEl.style.setProperty(
        '--messaging-available-height',
        `${availableHeight}px`,
      )

      const smallViewport = window.matchMedia(
        `(max-width:${theme.breakpoints.values.sm - 0.05}px)`,
      ).matches
      setIsCompactLayout(smallViewport || pageWidth < COMPACT_LAYOUT_MAX_WIDTH)

      const bottom = headerEl.getBoundingClientRect().bottom
      if (bottom <= 0) return
      document.documentElement.style.setProperty(
        '--page-header-bottom',
        `${bottom}px`,
      )
    }

    update()
    const ro = new ResizeObserver(update)
    ro.observe(headerEl)
    ro.observe(pageEl)
    window.addEventListener('resize', update)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', update)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayChannelList, channel?.id, isCompactLayout])

  if (loading) {
    return <ConversationLoader setLoading={() => setLoading(false)} />
  }

  const channelListProps = {
    key: listTab,
    filters,
    sort,
    options,
    Preview: ChannelPreview,
    Paginator: MessagingLoadMorePaginator,
    getLatestMessagePreview: getMessagingLatestMessagePreview,
    setActiveChannelOnMount:
      !isCompactLayout &&
      !initialChannelId &&
      !pathname.includes('/new-conversation'),
  }

  return displayChannelList ? (
    <Box
      ref={messagingPageRef}
      display="flex"
      flexDirection="column"
      className={clsx('messaging-page', {
        'messaging-page--group': isGroupConversation,
        'messaging-page--compact': isCompactLayout,
        'messaging-page--thread-view': isCompactLayout && showChannel,
        'messaging-page--list-view': isCompactLayout && showChannelList,
      })}
    >
      <Helmet>
        <script
          type="text/javascript"
          src="https://unpkg.com/mediainfo.js"
        ></script>
      </Helmet>
      <div ref={pageHeaderWrapperRef} className="messaging-page__header">
        <PageHeader
          title="Messages"
          style={{
            backgroundColor: 'white',
          }}
          actions={
            <NewConversationDialog trigger={newConversationDialogTrigger} />
          }
        />
      </div>
      <div className="messaging-layout str-chat__layout">
        <FollowUpSentProvider>
          <ChannelListContext.Provider value={channelListContextValue}>
            {showChannelList &&
              (isCompactLayout ? (
                <>
                  <ChannelListHeader />
                  <ChannelList
                    {...channelListProps}
                    showChannelSearch={false}
                  />
                </>
              ) : (
                <ChannelList
                  {...channelListProps}
                  showChannelSearch
                  ChannelSearch={ChannelListHeader}
                />
              ))}
            {showChannel && (
              <Channel
                channel={channel}
                Avatar={MessagingAvatar}
                Message={CustomMessage}
                Attachment={CustomAttachment}
                EmojiPicker={MessagingEmojiPicker}
                PinIndicator={CustomPinIndicator}
                SendButton={MessagingSendButton}
                SendToChannelCheckbox={MessagingSendToChannelCheckbox}
                MessageOptions={MessagingMessageOptions}
                MessageActions={MessagingMessageOptions}
                MessageActionsBox={MessagingMessageActionsBox}
                MessageTimestamp={() => null}
                FileUploadIcon={MessagingFileUploadIcon}
                ThreadHeader={MessagingThreadHeader}
                markReadOnMount={!user?.isAssumed}
                doMarkReadRequest={
                  user?.isAssumed ? () => undefined : undefined
                }
              >
                <AttachmentContextProvider>
                  <ChannelInner />
                  <Thread />
                </AttachmentContextProvider>
              </Channel>
            )}
          </ChannelListContext.Provider>
        </FollowUpSentProvider>
      </div>
    </Box>
  ) : (
    <EmptyChannel />
  )
})

export default Messenger
